import React, { useState, useEffect } from "react";
import {
  LayoutDashboard,
  FileText,
  ShoppingCart,
  DollarSign,
  Truck,
  Settings,
  Menu,
  PlusCircle,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardStats from "./components/DashboardStats";
import RFQManager from "./components/RFQManager";
import OrdersManager from "./components/OrdersManager";
import DeliveryManager from "./components/DeliveryManager";
import CreateDelivery from "./components/DeliveryFulfillmentFlow";
import ActionCenter from "./components/ActionCenter";
import NotificationCenter from "./components/NotificationCenter";
import { useNotification } from "../../context/NotificationContext";


/* ------------------------------------------------------------------ */
/* UI COMPONENTS (INLINE – NO EXTERNAL LIBS) */
/* ------------------------------------------------------------------ */

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* ---------- Button ---------- */
function Button({
  variant = "default",
  size = "md",
  className,
  children,
  ...props
}) {
  const variants = {
    default: "bg-primary text-primary-foreground hover:opacity-90",
    outline: "border border-border bg-transparent hover:bg-muted",
  };

  const sizes = {
    sm: "h-8 px-3 text-sm",
    md: "h-9 px-4",
  };

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium transition",
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/* ---------- Tabs ---------- */
function Tabs({ value, onValueChange, children, className }) {
  return (
    <div className={className}>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;

        return React.cloneElement(child, {
          activeValue: value,
          onValueChange,
        });
      })}
    </div>
  );
}

function TabsList({ children, className, activeValue, onValueChange }) {
  return (
    <div className={cn("inline-flex rounded-xl bg-muted p-1 gap-1", className)}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { activeValue, onValueChange })
          : child
      )}
    </div>
  );
}

function TabsTrigger({
  value,
  children,
  className,
  onValueChange,
  activeValue,
}) {
  const isActive = value === activeValue;

  return (
    <button
      onClick={() => onValueChange(value)}
      className={cn(
        "flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition",
        isActive
          ? "bg-card bg-gray-100 text-foreground"
          : "text-muted-foreground hover:text-foreground",
        className
      )}
    >
      {children}
    </button>
  );
}

function TabsContent({ value, activeValue, children, className }) {
  if (value !== activeValue) return null;
  return <div className={className}>{children}</div>;
}

/* ---------- Toaster (noop placeholder) ---------- */
function Toaster() {
  return null;
}

/* ------------------------------------------------------------------ */
/* APP */
/* ------------------------------------------------------------------ */

export default function QuickBuyOrderManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const { notifications } = useNotification();
  const [isRefreshing, setIsRefreshing] = useState(false);


  useEffect(() => {
    const tabFromState = location.state?.activeTab;
    setActiveTab(tabFromState || "overview");
  }, [location.state]);

  useEffect(() => {
  if (notifications?.length) {
    setIsRefreshing(true);

    const timer = setTimeout(() => {
      setIsRefreshing(false);
    }, 800); // small visible delay

    return () => clearTimeout(timer);
  }
}, [notifications?.length]);

const triggerLeftLoaderOnSamePage = () => {
  setIsRefreshing(true);

  // short timeout — navigation ayithe component unmount avuthundi
  setTimeout(() => {
    setIsRefreshing(false);
  }, 800);
};

  return (
<div className="h-screen bg-background ">
      <Toaster />

      {/* Main Layout */}
  <div className="flex h-full overflow-hidden">
 <div className="flex-1 p-6 min-w-0 flex flex-col overflow-hidden">
           {isRefreshing ? (
        <div className=" flex w-full  justify-center  bg-opacity-50 ">
    <div className=" flex space-x-2 w-full justify-center items-center h-20  ">
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
    </div>
  </div>
        ) : (
          <Tabs
           key={notifications?.length}
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-6"
          >
            <TabsList className="flex w-full max-w-[99%] items-center justify-between border border-gray-200 bg-white px-4">
              <TabsTrigger
                value="overview"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-600 
                      data-[state=active]:text-blue-600 
                      data-[state=active]:border-b-2 
                      data-[state=active]:border-blue-600"
              >
                <LayoutDashboard className="h-4 w-4" />
                Overview
              </TabsTrigger>

              <TabsTrigger
                value="rfqs"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-600 
                      data-[state=active]:text-blue-600 
                      data-[state=active]:border-b-2 
                      data-[state=active]:border-blue-600"
              >
                <FileText className="h-4 w-4" />
                RFQ Management
              </TabsTrigger>

              <TabsTrigger
                value="orders"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-600 
                      data-[state=active]:text-blue-600 
                      data-[state=active]:border-b-2 
                      data-[state=active]:border-blue-600"
              >
                <ShoppingCart className="h-4 w-4" />
                Order Management
              </TabsTrigger>

              <TabsTrigger
                value="deliverys"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-600 
                      data-[state=active]:text-blue-600 
                      data-[state=active]:border-b-2 
                      data-[state=active]:border-blue-600"
              >
                <Truck className="h-4 w-4" />
                Delivery Management
              </TabsTrigger>

              {/* 
              <button
                onClick={() => navigate("/bb/app/deliveryfullfillmentflow")}
                className="ml-2 flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 
                          text-sm font-semibold text-white hover:bg-red-700"
              >
                <PlusCircle className="h-4 w-4" />
                Create Delivery
              </button> 
              */}
            </TabsList>

           <TabsContent
  value="overview"
  className="flex-1 overflow-y-auto pr-2"
>
  <DashboardStats />
</TabsContent>

            <TabsContent value="rfqs">
              <RFQManager />
            </TabsContent>

            <TabsContent value="orders">
              <OrdersManager />
            </TabsContent>

            <TabsContent value="deliverys">
              <DeliveryManager />
            </TabsContent>
          </Tabs>
        )}
        </div>

       <aside className="sticky top-0 h-[calc(100vh-4px)] max-h-[94vh]
                  w-[clamp(18rem,22vw,21rem)]
                  border-l p-4 shrink-0">
          <ActionCenter onViewClick={triggerLeftLoaderOnSamePage}/>
        </aside>
      </div>
    </div>
  );
}
